import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Barcode, Link2, Loader2, QrCode, Search, X } from 'lucide-react';
import { getUsers } from '@/api/endpoints/users';
import { Modal } from '@/components/common/Modal';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useCriarCobranca, usePrecosDosPlanos } from '@/hooks/useCobrancas';
import type {
  CicloCobranca,
  MetodoCobranca,
  NovaCobrancaInput,
  PrecoPlano,
} from '@/api/endpoints/cobrancas';
import { centavosDeTexto, mensagemDeErro, reaisDeCentavos } from './formato';

export interface ProdutorEscolhido {
  id: string;
  name: string;
  phone: string;
}

const inputCls =
  'w-full rounded-lg border border-border bg-bg-main px-3 py-2.5 text-sm text-text-title outline-none transition-colors placeholder:text-text-hint focus:border-primary';

const METODOS: { valor: MetodoCobranca; rotulo: string; icone: typeof QrCode; apoio: string }[] = [
  { valor: 'PIX', rotulo: 'Pix', icone: QrCode, apoio: 'Já sai com o copia e cola' },
  { valor: 'BOLETO', rotulo: 'Boleto', icone: Barcode, apoio: 'Precisa de CPF/CNPJ' },
  { valor: 'CARTAO', rotulo: 'Só o link', icone: Link2, apoio: 'Cliente escolhe como pagar' },
];

const PARCELA_MINIMA_CENTAVOS = 1000;

function Campo({
  rotulo,
  dica,
  children,
}: {
  rotulo: string;
  dica?: ReactNode;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[13px] font-medium text-text-secondary">{rotulo}</span>
      {children}
      {dica && <span className="mt-1 block text-xs text-text-hint">{dica}</span>}
    </label>
  );
}

function hojeMais(dias: number) {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  return d.toISOString().slice(0, 10);
}

const chavePreco = (p: Pick<PrecoPlano, 'planoCodigo' | 'ciclo'>) => `${p.planoCodigo}:${p.ciclo}`;

function BuscaProdutor({
  escolhido,
  onEscolher,
}: {
  escolhido: ProdutorEscolhido | null;
  onEscolher: (p: ProdutorEscolhido | null) => void;
}) {
  const [texto, setTexto] = useState('');
  const [busca, setBusca] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setBusca(texto.trim()), 300);
    return () => clearTimeout(t);
  }, [texto]);
  const { data, isFetching } = useQuery({
    queryKey: ['users', 'busca-cobranca', busca],
    queryFn: () => getUsers({ search: busca, onlyProducers: true, limit: 5 }),
    enabled: busca.length >= 2,
    staleTime: 1000 * 60,
  });
  const resultados = busca.length >= 2 ? (data?.users ?? []) : [];

  if (escolhido) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-primary/40 bg-primary/5 px-3 py-2.5">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-text-title">{escolhido.name}</p>
          <p className="truncate text-xs text-text-secondary">{escolhido.phone}</p>
        </div>
        <button
          type="button"
          onClick={() => onEscolher(null)}
          aria-label="Trocar produtor"
          className="rounded-md p-1 text-text-secondary hover:bg-muted hover:text-text-title"
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <Search size={16} className="absolute left-3 top-3 text-text-hint" aria-hidden="true" />
      <input
        className={cn(inputCls, 'pl-9')}
        placeholder="Nome ou telefone do produtor"
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
      />
      {isFetching && (
        <Loader2 size={16} className="absolute right-3 top-3 animate-spin text-text-hint" />
      )}
      {resultados.length > 0 && (
        <ul className="mt-1 overflow-hidden rounded-lg border border-border bg-bg-elevated shadow-card">
          {resultados.map((u) => (
            <li key={u.id}>
              <button
                type="button"
                onClick={() => {
                  onEscolher({ id: u.id, name: u.name, phone: u.phone });
                  setTexto('');
                }}
                className="flex w-full flex-col px-3 py-2 text-left hover:bg-muted"
              >
                <span className="text-sm font-medium text-text-title">{u.name}</span>
                <span className="text-xs text-text-secondary">{u.phone}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function NovaCobrancaModal({
  aberto,
  produtorInicial,
  onClose,
  onCriada,
}: {
  aberto: boolean;
  produtorInicial?: ProdutorEscolhido | null;
  onClose: () => void;
  onCriada: (id: number) => void;
}) {
  const { data: precos = [] } = usePrecosDosPlanos();
  const criar = useCriarCobranca();

  const [metodo, setMetodo] = useState<MetodoCobranca>('PIX');
  const [tipo, setTipo] = useState<'plano' | 'livre'>('plano');
  const [planoEscolhido, setPlanoEscolhido] = useState<string | null>(null);
  const [valorTexto, setValorTexto] = useState('');
  const [descricao, setDescricao] = useState('');
  const [produtor, setProdutor] = useState<ProdutorEscolhido | null>(produtorInicial ?? null);
  const [clienteNome, setClienteNome] = useState('');
  const [documento, setDocumento] = useState('');
  const [email, setEmail] = useState('');
  const [vencimento, setVencimento] = useState(hojeMais(3));
  const [parcelas, setParcelas] = useState(1);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (aberto) setProdutor(produtorInicial ?? null);
  }, [aberto, produtorInicial]);

  const preco = precos.find((p) => chavePreco(p) === planoEscolhido) ?? null;
  const valorDigitado = centavosDeTexto(valorTexto);
  const valorFinal =
    tipo === 'plano' ? (valorDigitado ?? preco?.valorCentavos ?? null) : valorDigitado;
  const maxParcelas = Math.max(
    1,
    Math.min(12, Math.floor((valorFinal ?? 0) / PARCELA_MINIMA_CENTAVOS))
  );

  const planosPorNome = useMemo(() => {
    const grupos = new Map<string, PrecoPlano[]>();
    precos.forEach((p) => grupos.set(p.planoNome, [...(grupos.get(p.planoNome) ?? []), p]));
    return Array.from(grupos.entries());
  }, [precos]);

  const limpar = () => {
    setMetodo('PIX');
    setTipo('plano');
    setPlanoEscolhido(null);
    setValorTexto('');
    setDescricao('');
    setClienteNome('');
    setDocumento('');
    setEmail('');
    setVencimento(hojeMais(3));
    setParcelas(1);
    setErro(null);
  };

  const fechar = () => {
    limpar();
    onClose();
  };

  const validar = (): string | null => {
    if (tipo === 'plano' && !preco) return 'Escolha o plano.';
    if (tipo === 'plano' && !produtor) return 'Plano precisa de um produtor para ser ativado.';
    if (valorFinal == null || valorFinal < 100) return 'Valor mínimo de R$ 1,00.';
    const doc = documento.replace(/\D/g, '');
    if (doc && doc.length !== 11 && doc.length !== 14) return 'CPF ou CNPJ incompleto.';
    if (metodo === 'BOLETO' && !doc) return 'Boleto precisa do CPF ou CNPJ do pagador.';
    if (metodo === 'BOLETO' && !clienteNome.trim() && !produtor)
      return 'Boleto precisa do nome do pagador.';
    return null;
  };

  const enviar = (e: FormEvent) => {
    e.preventDefault();
    const problema = validar();
    if (problema) {
      setErro(problema);
      return;
    }
    setErro(null);
    const dados: NovaCobrancaInput = {
      metodo,
      cliente: {
        nome: clienteNome.trim() || undefined,
        email: email.trim() || undefined,
        documento: documento.replace(/\D/g, '') || undefined,
      },
    };
    if (produtor) dados.produtorUserId = Number(produtor.id);
    if (tipo === 'plano' && preco) {
      dados.planoCodigo = preco.planoCodigo;
      dados.ciclo = preco.ciclo as CicloCobranca;
      if (valorDigitado != null && valorDigitado !== preco.valorCentavos)
        dados.valorCentavos = valorDigitado;
    } else {
      dados.valorCentavos = valorFinal ?? undefined;
      if (descricao.trim()) dados.descricao = descricao.trim();
    }
    if (metodo === 'BOLETO') dados.vencimento = vencimento;
    dados.parcelasMax = Math.min(parcelas, maxParcelas);

    criar.mutate(dados, {
      onSuccess: (cobranca) => {
        limpar();
        onCriada(cobranca.id);
      },
      onError: (err) => setErro(mensagemDeErro(err, 'Não deu para gerar a cobrança.')),
    });
  };

  return (
    <Modal isOpen={aberto} onClose={fechar} title="Nova cobrança" size="md">
      <form onSubmit={enviar} className="flex flex-col gap-5 p-6 max-md:p-4">
        <fieldset>
          <legend className="mb-2 text-[13px] font-medium text-text-secondary">
            Forma de pagamento
          </legend>
          <div className="grid grid-cols-3 gap-2">
            {METODOS.map((m) => {
              const ativo = metodo === m.valor;
              return (
                <button
                  key={m.valor}
                  type="button"
                  aria-pressed={ativo}
                  onClick={() => setMetodo(m.valor)}
                  className={cn(
                    'flex flex-col items-center gap-1 rounded-xl border px-2 py-3 text-center transition-colors',
                    ativo
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-text-secondary hover:bg-muted'
                  )}
                >
                  <m.icone size={20} />
                  <span className="text-sm font-semibold">{m.rotulo}</span>
                  <span className="text-[11px] leading-tight text-text-hint max-sm:hidden">
                    {m.apoio}
                  </span>
                </button>
              );
            })}
          </div>
        </fieldset>

        <fieldset>
          <div className="mb-2 flex items-center justify-between">
            <legend className="text-[13px] font-medium text-text-secondary">
              O que está cobrando
            </legend>
            <div
              className="flex rounded-lg border border-border bg-card p-0.5 text-xs"
              role="group"
            >
              {(['plano', 'livre'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  aria-pressed={tipo === t}
                  onClick={() => {
                    setTipo(t);
                    setValorTexto('');
                  }}
                  className={cn(
                    'rounded-md px-2.5 py-1 font-medium transition-colors',
                    tipo === t ? 'bg-primary text-bg-main' : 'text-text-secondary'
                  )}
                >
                  {t === 'plano' ? 'Plano' : 'Valor livre'}
                </button>
              ))}
            </div>
          </div>

          {tipo === 'plano' ? (
            <div className="flex flex-col gap-3">
              {planosPorNome.length === 0 && (
                <p className="text-sm text-text-secondary">Nenhum plano com preço cadastrado.</p>
              )}
              {planosPorNome.map(([nome, opcoes]) => (
                <div key={nome}>
                  <p className="mb-1.5 text-sm font-semibold text-text-title">{nome}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {opcoes.map((p) => {
                      const ativo = planoEscolhido === chavePreco(p);
                      return (
                        <button
                          key={chavePreco(p)}
                          type="button"
                          aria-pressed={ativo}
                          onClick={() => {
                            setPlanoEscolhido(chavePreco(p));
                            setValorTexto('');
                          }}
                          className={cn(
                            'flex flex-col items-start rounded-xl border px-3 py-2.5 text-left transition-colors',
                            ativo ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted'
                          )}
                        >
                          <span className="text-xs text-text-secondary">
                            {p.ciclo === 'anual' ? 'Anual, à vista' : 'Mensal'}
                          </span>
                          <span className="text-base font-bold tabular-nums text-text-title">
                            {reaisDeCentavos(p.valorCentavos)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              {preco && (
                <Campo
                  rotulo="Valor com desconto (opcional)"
                  dica="Em branco cobra o preço da tabela."
                >
                  <input
                    className={inputCls}
                    inputMode="decimal"
                    placeholder={reaisDeCentavos(preco.valorCentavos)}
                    value={valorTexto}
                    onChange={(e) => setValorTexto(e.target.value)}
                  />
                </Campo>
              )}
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <Campo rotulo="Valor (R$)">
                <input
                  className={inputCls}
                  inputMode="decimal"
                  placeholder="150,00"
                  value={valorTexto}
                  onChange={(e) => setValorTexto(e.target.value)}
                />
              </Campo>
              <Campo rotulo="Descrição">
                <input
                  className={inputCls}
                  maxLength={140}
                  placeholder="Ex.: instalação do bastão"
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                />
              </Campo>
            </div>
          )}
        </fieldset>

        <Campo
          rotulo={tipo === 'plano' ? 'Produtor' : 'Produtor (opcional)'}
          dica={
            tipo === 'plano'
              ? 'O plano é ativado sozinho no cadastro dele quando o pagamento cair.'
              : 'Liga a cobrança ao cadastro e preenche nome e telefone.'
          }
        >
          <BuscaProdutor escolhido={produtor} onEscolher={setProdutor} />
        </Campo>

        <div className="grid gap-3 sm:grid-cols-2">
          <Campo
            rotulo={
              metodo === 'BOLETO' && !produtor ? 'Nome do pagador' : 'Nome do pagador (opcional)'
            }
          >
            <input
              className={inputCls}
              maxLength={120}
              placeholder={produtor?.name ?? 'Nome completo'}
              value={clienteNome}
              onChange={(e) => setClienteNome(e.target.value)}
            />
          </Campo>
          <Campo rotulo={metodo === 'BOLETO' ? 'CPF ou CNPJ' : 'CPF ou CNPJ (opcional)'}>
            <input
              className={inputCls}
              inputMode="numeric"
              maxLength={18}
              placeholder="Só números"
              value={documento}
              onChange={(e) => setDocumento(e.target.value)}
            />
          </Campo>
          <Campo rotulo="E-mail (opcional)" dica="A AbacatePay manda o comprovante para ele.">
            <input
              className={inputCls}
              type="email"
              maxLength={160}
              placeholder="cliente@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Campo>
          {metodo === 'BOLETO' && (
            <Campo rotulo="Vencimento">
              <input
                className={inputCls}
                type="date"
                min={hojeMais(1)}
                value={vencimento}
                onChange={(e) => setVencimento(e.target.value)}
              />
            </Campo>
          )}
          {maxParcelas > 1 && (
            <Campo rotulo="Cartão em até" dica="Parcela mínima de R$ 10,00.">
              <select
                className={inputCls}
                value={Math.min(parcelas, maxParcelas)}
                onChange={(e) => setParcelas(Number(e.target.value))}
              >
                {Array.from({ length: maxParcelas }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>
                    {n === 1 ? 'À vista' : `${n}x`}
                  </option>
                ))}
              </select>
            </Campo>
          )}
        </div>

        {erro && (
          <p role="alert" className="rounded-lg bg-error/10 px-3 py-2 text-sm text-error">
            {erro}
          </p>
        )}

        <div className="flex items-center justify-between gap-3 border-t border-border pt-4 max-md:flex-col max-md:items-stretch">
          <p className="text-sm text-text-secondary">
            Total:{' '}
            <span className="text-lg font-bold tabular-nums text-text-title">
              {valorFinal ? reaisDeCentavos(valorFinal) : 'R$ 0,00'}
            </span>
          </p>
          <Button type="submit" disabled={criar.isPending}>
            {criar.isPending && <Loader2 size={16} className="animate-spin" />}
            Gerar cobrança
          </Button>
        </div>
      </form>
    </Modal>
  );
}
